import axios from 'axios';
import { addCSRFToken } from '../utils/csrfHelper';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.wormser-maxicar.de/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  timeout: 30000, // 30 seconds timeout
});

// Helper function to get CSRF token from cookie
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Add request interceptor to handle CSRF token and auth token
api.interceptors.request.use(
  async (config) => {
    // Always include withCredentials for all requests
    config.withCredentials = true;
    
    // Check if this is an admin request
    const isAdminRequest = config.url?.includes('/admin/');
    
    if (isAdminRequest) {
      console.log('🔑 Admin request detected:', config.method?.toUpperCase(), config.url);
      
      // Check admin authentication
      const adminSession = localStorage.getItem('adminSession');
      const adminUser = localStorage.getItem('adminUser');
      
      if (!adminSession || !adminUser) {
        console.error('🚫 Admin request blocked: No admin session found');
        throw new Error('Admin authentication required');
      }
      
      console.log('✅ Admin session verified for request');
    }
    
    // Add auth token if available (for regular user requests)
    const authToken = localStorage.getItem('authToken');
    if (authToken && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Token ${authToken}`;
    }
    
    // Add CSRF token for non-GET requests
    if (config.method !== 'get') {
      const csrfToken = getCookie('csrftoken');
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
        if (isAdminRequest) {
          console.log('🔒 CSRF token added to admin request');
        }
      } else if (isAdminRequest) {
        console.warn('⚠️ No CSRF token found for admin request');
        
        // Try to get CSRF token from sessionStorage (stored during admin login)
        const sessionCsrfToken = sessionStorage.getItem('csrf_token');
        if (sessionCsrfToken) {
          config.headers['X-CSRFToken'] = sessionCsrfToken;
          console.log('🔒 Session CSRF token added to admin request');
        }
      }
    }
    
    // Set additional headers for CORS compliance
    config.headers['Accept'] = 'application/json';
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for basic error handling
api.interceptors.response.use(
  (response) => {
    console.log(`✅ [${response.config.method?.toUpperCase()}] ${response.config.url} - ${response.status} ${response.statusText || ''}`);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Log the error details
    console.error(`❌ [${originalRequest?.method?.toUpperCase()}] ${originalRequest?.url} - ${error.response?.status || 'No Response'}`);
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.log('🛑 Session expired or invalid token');
      
      // Clear auth data
      localStorage.removeItem('authToken');
      delete api.defaults.headers.common['Authorization'];
      
      // Redirect to login page if we're not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session_expired=true';
      }
    }
    
    // Handle 403 Forbidden errors
    if (error.response?.status === 403) {
      console.log('🚫 Forbidden - CSRF token may be missing or invalid');
    }
    
    // Handle network errors
    if (error.code === 'ECONNABORTED') {
      console.error('⏱️ Request timeout - the server took too long to respond');
    } else if (!error.response) {
      console.error('🌐 Network error - unable to reach the server');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Development mode: Consider starting the backend server');
      }
    }

    // Reject the promise with the error
    return Promise.reject(error);
  }
);

// Remove the duplicate code block
export const bookingAPI = {
  create: async (data) => {
    try {
      console.log('🚀 Starting booking creation...');
      
      // First attempt with CSRF token
      return await api.post('/bookings/', data);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('CSRF')) {
        console.warn('⚠️ CSRF error detected, attempting fallback method...');
        
        // Fallback: Direct axios call without CSRF for bookings
        try {
          const response = await axios.post(
            `${API_BASE_URL}/bookings/`,
            data,
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              withCredentials: false, // Don't send cookies to avoid CSRF requirement
              timeout: 30000
            }
          );
          console.log('✅ Booking created successfully using fallback method');
          return response;
        } catch (fallbackError) {
          console.error('❌ Fallback booking method also failed:', fallbackError);
          throw error; // Throw original CSRF error
        }
      }
      throw error;
    }
  },
  getAll: () => api.get('/bookings/'),
  getById: (id) => api.get(`/bookings/${id}/`),
  getUserBookings: (userId) => api.get(`/bookings/user/${userId}/`),
  getUserFavorites: async (userId) => {
    try {
      return await api.get(`/user/${userId}/favorites/`);
    } catch (error) {
      console.warn('Favorites API not available, using fallback data');
      // Return mock favorites data as fallback
      return {
        data: {
          success: true,
          favorites: [
            {
              id: 1,
              name: "Home",
              address: "123 Main Street, Worms",
              type: "home",
              created_at: new Date().toISOString()
            },
            {
              id: 2,
              name: "Work",
              address: "456 Business Ave, Worms",
              type: "work", 
              created_at: new Date().toISOString()
            }
          ]
        }
      };
    }
  },
  cancel: (bookingId) => api.patch(`/bookings/${bookingId}/`, { status: 'cancelled' }),
  search: (query, type = 'all') => api.get('/bookings/search/', {
    params: { query, type }
  }),
};

export const servicesAPI = {
  getAll: () => api.get('/services/'),
};

export const routesAPI = {
  getAll: () => api.get('/routes/'),
};

export const translationsAPI = {
  get: (lang) => api.get(`/translations/?lang=${lang}`),
};

export const authAPI = {
  getCsrfToken: () => api.get('/auth/csrf/'),
  register: (userData) => api.post('/register/', userData),
  requestOtp: async (userData) => {
    try {
      console.log('🚀 Starting OTP request process for:', userData.email);
      
      // Create dedicated axios instance for OTP request
      const otpAxios = axios.create({
        baseURL: API_BASE_URL,
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });
      
      const response = await otpAxios.post('/request-otp/', userData);
      
      console.log('✅ OTP request successful');
      return response;
    } catch (error) {
      console.error('❌ OTP request failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
  verifyOtp: async (otpData) => {
    try {
      console.log('🚀 Starting OTP verification process...');
      
      // Create dedicated axios instance for OTP verification
      const otpAxios = axios.create({
        baseURL: API_BASE_URL,
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });
      
      const response = await otpAxios.post('/verify-otp/', otpData);
      
      console.log('✅ OTP verification successful');
      
      // If verification is successful, store the auth token
      if (response.data.success && response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        api.defaults.headers.common['Authorization'] = `Token ${response.data.token}`;
        
        // Store user data
        if (response.data.user) {
          localStorage.setItem('userData', JSON.stringify(response.data.user));
        }
      }
      
      return response;
    } catch (error) {
      console.error('❌ OTP verification failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
  login: async (credentials) => {
    try {
      console.log('🚀 Starting user login process for:', credentials.email);
      
      // Create dedicated axios instance for user login
      const userAxios = axios.create({
        baseURL: API_BASE_URL,
        timeout: 30000,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });
      
      // Get CSRF token using helper
      await addCSRFToken(userAxios);
      
      console.log('🔑 Making user login request...');
      const response = await userAxios.post('/login/', credentials);
      
      console.log('✅ User login response received:', response.status);
      
      if (response.data.success && response.data.token) {
        console.log('✅ User login successful');
        
        // Store auth token
        localStorage.setItem('authToken', response.data.token);
        api.defaults.headers.common['Authorization'] = `Token ${response.data.token}`;
        
        // Store user data
        if (response.data.user) {
          localStorage.setItem('userData', JSON.stringify(response.data.user));
        }
      }
      
      return response;
    } catch (error) {
      console.error('❌ User login failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Clear any stale auth data on login failure
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      delete api.defaults.headers.common['Authorization'];
      
      throw error;
    }
  },
  adminLogin: async (credentials) => {
    try {
      console.log('🚀 Starting admin login process...');
      console.log('📌 Environment:', process.env.NODE_ENV, '| API URL:', API_BASE_URL);
      console.log('📌 Admin login credentials:', { email: credentials.email, hasPassword: !!credentials.password });
      
      // Create dedicated axios instance for admin login
      const adminAxios = axios.create({
        baseURL: API_BASE_URL,
        timeout: 30000,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });
      
      // Get CSRF token using helper
      await addCSRFToken(adminAxios);
      
      console.log('🔑 Making admin login request...');
      const response = await adminAxios.post('/auth/login/', credentials);
      
      console.log('✅ Admin login response received:', response.status);
      
      if (response.data.success) {
        console.log('✅ Admin login successful');
        
        // Store session data
        localStorage.setItem('adminSession', 'true');
        localStorage.setItem('adminUser', JSON.stringify(response.data.user));
        
        // Store CSRF token if provided
        if (response.data.csrfToken) {
          sessionStorage.setItem('csrf_token', response.data.csrfToken);
          console.log('✅ CSRF token stored from login response');
        }
        
        // Set default authorization header for future requests
        if (response.data.token && response.data.token !== 'session-authenticated') {
          api.defaults.headers.common['Authorization'] = `Token ${response.data.token}`;
        }
      }
      
      return response;
    } catch (error) {
      console.error('❌ Admin login failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Clear any stale session data on login failure
      localStorage.removeItem('adminSession');
      localStorage.removeItem('adminUser');
      sessionStorage.removeItem('csrf_token');
      
      throw error;
    }
  },
  logout: () => api.post('/auth/logout/'),
  checkSession: () => api.get('/auth/check/'),
};

// Helper function to refresh CSRF token for admin operations
const refreshCSRFToken = async () => {
  try {
    console.log('🔄 Refreshing CSRF token for admin operations...');
    const response = await api.get('/auth/csrf/');
    const csrfToken = response.data.csrfToken || getCookie('csrftoken');
    if (csrfToken) {
      sessionStorage.setItem('csrf_token', csrfToken);
      console.log('✅ CSRF token refreshed and stored');
      return csrfToken;
    }
  } catch (error) {
    console.error('❌ Failed to refresh CSRF token:', error);
  }
  return null;
};

export const adminAPI = {
  getBookings: () => api.get('/admin/bookings/'),
  getUsers: () => api.get('/admin/users/'),
  createUser: (userData) => api.post('/admin/users/', userData),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}/`),
  checkSession: () => api.get('/auth/check/'),
  getDrivers: () => api.get('/admin/drivers/'),
  createDriver: (driverData) => api.post('/admin/drivers/', driverData),
  deleteDriver: (driverId) => api.delete(`/admin/drivers/${driverId}/`),
  assignDriver: (bookingId, driverId) => {
    return api.post(`/admin/bookings/${bookingId}/assign/`, { driver_id: driverId });
  },
  getNotifications: () => api.get('/admin/notifications/'),
  getDriverApplications: () => api.get('/admin/driver-applications/'),
  approveDriverApplication: async (applicationId, action, adminNotes) => {
    try {
      console.log('🚀 Starting driver application approval process...');
      console.log('📋 Application ID:', applicationId);
      console.log('🎯 Action:', action);
      console.log('📝 Admin Notes:', adminNotes);
      
      // Create dedicated axios instance for admin operations
      const adminAxios = axios.create({
        baseURL: API_BASE_URL,
        timeout: 30000,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });
      
      // Get CSRF token for admin operations
      await addCSRFToken(adminAxios);
      
      // Add admin session token if available
      const adminSession = localStorage.getItem('adminSession');
      if (adminSession) {
        console.log('🔑 Admin session found, adding to headers');
      }
      
      console.log('📤 Making admin approval request...');
      const response = await adminAxios.post(`/admin/driver-applications/${applicationId}/approve/`, {
        action,
        admin_notes: adminNotes
      });
      
      console.log('✅ Driver application approval response:', response.data);
      return response;
      
    } catch (error) {
      console.error('❌ Driver application approval failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
  // Enhanced Analytics APIs
  getAnalytics: (timeRange = '30') => api.get(`/admin/analytics/?days=${timeRange}`),
  getRevenueAnalytics: (timeRange = '30') => api.get(`/admin/analytics/revenue/?days=${timeRange}`),
  getDriverPerformance: () => api.get('/admin/analytics/drivers/'),
  getPopularRoutes: () => api.get('/admin/analytics/routes/'),
  getPeakHours: () => api.get('/admin/analytics/peak-hours/'),
  exportAnalyticsReport: (format = 'csv', timeRange = '30') => api.get(`/admin/analytics/export/?format=${format}&days=${timeRange}`, { responseType: 'blob' }),
  
  // User Messages APIs with enhanced authentication
  getMessages: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.subject && filters.subject !== 'all') params.append('subject', filters.subject);
      
      console.log('📋 Fetching admin messages with filters:', filters);
      const response = await api.get(`/admin/messages/?${params.toString()}`);
      console.log('✅ Admin messages fetched successfully');
      return response;
    } catch (error) {
      console.error('❌ Admin messages fetch failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
  
  replyToMessage: async (messageId, reply) => {
    try {
      console.log('💬 Sending admin reply to message:', messageId);
      const response = await api.post(`/admin/messages/${messageId}/reply/`, { reply });
      console.log('✅ Admin reply sent successfully');
      return response;
    } catch (error) {
      console.error('❌ Admin reply failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
  
  updateMessageStatus: async (messageId, status) => {
    try {
      console.log('🔄 Updating message status:', { messageId, status });
      
      // Check admin authentication
      const adminSession = localStorage.getItem('adminSession');
      const adminUser = localStorage.getItem('adminUser');
      
      if (!adminSession || !adminUser) {
        throw new Error('Admin authentication required. Please log in as admin.');
      }
      
      console.log('🔑 Admin authentication verified');
      
      try {
        const response = await api.patch(`/admin/messages/${messageId}/status/`, { status });
        console.log('✅ Message status updated successfully');
        return response;
      } catch (firstAttemptError) {
        // If 403 error, try refreshing CSRF token and retry
        if (firstAttemptError.response?.status === 403) {
          console.warn('⚠️ First attempt failed with 403, trying to refresh CSRF token...');
          
          const newCsrfToken = await refreshCSRFToken();
          if (newCsrfToken) {
            console.log('🔁 Retrying with fresh CSRF token...');
            const retryResponse = await api.patch(`/admin/messages/${messageId}/status/`, { status });
            console.log('✅ Message status updated successfully on retry');
            return retryResponse;
          }
        }
        throw firstAttemptError;
      }
    } catch (error) {
      console.error('❌ Admin status update failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url
      });
      
      // Enhanced error handling for 403 errors
      if (error.response?.status === 403) {
        console.error('🚫 403 Forbidden - Possible causes:');
        console.error('1. Admin session expired');
        console.error('2. CSRF token missing or invalid');
        console.error('3. Insufficient admin permissions');
        
        // Check admin session status
        const adminSession = localStorage.getItem('adminSession');
        const adminUser = localStorage.getItem('adminUser');
        console.error('Admin session status:', { hasSession: !!adminSession, hasUser: !!adminUser });
        
        // Check CSRF token status
        const csrfCookie = getCookie('csrftoken');
        const sessionCsrf = sessionStorage.getItem('csrf_token');
        console.error('CSRF token status:', { hasCookie: !!csrfCookie, hasSession: !!sessionCsrf });
      }
      
      throw error;
    }
  },
  
  sendWelcomeEmail: async (userId) => {
    try {
      console.log('📧 Sending welcome email to user:', userId);
      
      // Check admin authentication
      const adminSession = localStorage.getItem('adminSession');
      const adminUser = localStorage.getItem('adminUser');
      
      if (!adminSession || !adminUser) {
        throw new Error('Admin authentication required. Please log in as admin.');
      }
      
      const response = await api.post('/admin/send-welcome-email/', { user_id: userId });
      console.log('✅ Welcome email sent successfully');
      return response;
    } catch (error) {
      console.error('❌ Welcome email send failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
};

export const notificationAPI = {
  getNotifications: (email) => api.get('/notifications/', { params: { email } }),
  getUserNotifications: (email) => api.get('/user/notifications/', { params: { email } }),
  markAsRead: (notificationId) => api.post(`/notifications/${notificationId}/read/`),
  driverResponse: (responseData) => api.post('/driver/response/', responseData),
};

export const analyticsAPI = {
  // Legacy analytics functions
  trackEvent: (eventData) => api.post('/analytics/track/', eventData),
  getMetrics: (timeRange = '7') => api.get(`/analytics/metrics/?days=${timeRange}`),
  getRealTimeStats: () => api.get('/analytics/realtime/'),
  
  // New comprehensive visit tracking functions
  trackVisit: (visitData) => api.post('/analytics/track-visit/', visitData),
  trackExit: (exitData) => api.post('/analytics/track-exit/', exitData),
  trackConversion: (conversionData) => api.post('/analytics/track-conversion/', conversionData),
  
  // Admin analytics functions
  getOverview: (days = 30) => api.get(`/admin/analytics/overview/?days=${days}`),
  getRealTimeMetrics: () => api.get('/admin/analytics/real-time/'),
  getConversionFunnel: (days = 30) => api.get(`/admin/analytics/conversion-funnel/?days=${days}`),
  getVisitorSessions: (page = 1, perPage = 20, device = 'all') => 
    api.get(`/admin/analytics/visitor-sessions/?page=${page}&per_page=${perPage}&device=${device}`),
  getCallNowClicks: (days = 30, page = 1, perPage = 20) =>
    api.get(`/admin/analytics/call-now-clicks/?days=${days}&page=${page}&per_page=${perPage}`)
};

export const userAPI = {
  getNotifications: (email) => api.get('/user/notifications/', { params: { email } }),
  getMessages: () => api.get('/messages/user/'),
  sendMessage: async (messageData) => {
    try {
      console.log('📨 Sending message data:', messageData);
      const response = await api.post('/messages/send/', messageData);
      console.log('✅ Message sent successfully:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Message send failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        sentData: error.config?.data
      });
      
      // Log the detailed error response
      if (error.response?.data) {
        console.error('📋 Backend error details:', JSON.stringify(error.response.data, null, 2));
      }
      throw error;
    }
  },
  
  // Driver Chat APIs
  getDriverChats: () => api.get('/user/driver-chats/'),
  sendDriverMessage: async (chatData) => {
    try {
      console.log('🚗 Sending driver chat message:', chatData);
      const response = await api.post('/user/driver-chat/send/', chatData);
      console.log('✅ Driver message sent successfully');
      return response;
    } catch (error) {
      console.error('❌ Driver message send failed:', error);
      throw error;
    }
  },
  getChatHistory: (bookingId) => api.get(`/user/driver-chat/${bookingId}/history/`),
  getActiveBookings: () => api.get('/user/active-bookings/'),
  sendMessageToDriver: async (messageData) => {
    try {
      console.log('📨 Sending message to driver:', messageData);
      const response = await api.post('/user/message-to-driver/', messageData);
      console.log('✅ Message sent to driver successfully');
      return response;
    } catch (error) {
      console.error('❌ Failed to send message to driver:', error);
      throw error;
    }
  },
};

// Driver Chat APIs
export const driverChatAPI = {
  getChats: () => api.get('/driver/chats/'),
  sendMessage: async (chatData) => {
    try {
      console.log('🚕 Driver sending chat message:', chatData);
      const response = await api.post('/driver/chat/send/', chatData);
      console.log('✅ Driver chat message sent successfully');
      return response;
    } catch (error) {
      console.error('❌ Driver chat message send failed:', error);
      throw error;
    }
  },
  getChatHistory: (bookingId) => api.get(`/driver/chat/${bookingId}/history/`),
  markAsRead: (chatId) => api.post(`/driver/chat/${chatId}/read/`),
  getUserMessages: () => api.get('/driver/user-messages/'),
};

export const driverAPI = {
  applyAsDriver: async (applicationData) => {
    try {
      console.log('🚀 Starting driver application process for:', applicationData.email);
      
      // Make direct axios call to avoid interceptor complications for driver application
      const response = await axios.post(
        `${API_BASE_URL}/apply-driver/`,
        applicationData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          withCredentials: false, // Don't send cookies for public endpoint
          timeout: 30000
        }
      );
      
      console.log('✅ Driver application submitted successfully');
      return response;
    } catch (error) {
      console.error('❌ Driver application failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
  login: async (email, license_number) => {
    try {
      console.log('🚀 Starting driver login process for:', email);
      
      // Make direct axios call to avoid interceptor complications for driver login
      const response = await axios.post(
        `${API_BASE_URL}/driver/login/`,
        { email, license_number },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          withCredentials: false, // Don't send cookies for public endpoint
          timeout: 30000
        }
      );
      
      console.log('✅ Driver login response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Driver login failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
  getApplications: () => api.get('/admin/driver-applications/'),
  approveApplication: (applicationId, action, adminNotes) => api.post(`/admin/driver-applications/${applicationId}/approve/`, {
    action,
    admin_notes: adminNotes
  }),
};

// Feedback API
export const feedbackAPI = {
  getAll: async () => {
    try {
      console.log('📝 Making feedback API request to:', `${API_BASE_URL}/feedback/`);
      return await api.get('/feedback/');
    } catch (error) {
      console.error('❌ Feedback API error:', error.response?.status, error.response?.statusText);
      console.error('📊 Error details:', {
        url: error.config?.url,
        method: error.config?.method,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      // For server errors, provide more specific error handling
      if (error.response?.status >= 500) {
        console.log('📁 Server error detected - feedback service may be experiencing issues');
        console.log('🔍 This could be due to database connection or model serialization errors');
      }
      throw error;
    }
  },
  submit: (feedbackData) => api.post('/feedback/submit/', feedbackData),
  getAdminFeedback: () => api.get('/admin/feedback/'),
  moderate: (feedbackId, action) => api.post(`/admin/feedback/${feedbackId}/moderate/`, { action }),
};

// Convenience function for submitting feedback
export const submitFeedback = async (feedbackData) => {
  try {
    const response = await feedbackAPI.submit(feedbackData);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to submit feedback:', error);
    throw error.response?.data || { error: 'Failed to submit feedback' };
  }
};

// Convenience function for getting feedback
export const getFeedback = async () => {
  try {
    console.log('📝 Fetching feedback data...');
    const response = await feedbackAPI.getAll();
    console.log('✅ Feedback data retrieved successfully:', response.data?.length, 'items');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get feedback:', error);
    
    // Enhanced error logging for production debugging
    if (error.response?.status >= 500) {
      console.error('🚨 Server Error (500) - Backend feedback API is experiencing issues');
      console.error('🔍 Possible causes:');
      console.error('  1. Database connection issues');
      console.error('  2. Model serialization errors (check get_star_display method)');
      console.error('  3. Backend deployment issues');
      console.error('📊 Error response:', error.response?.data);
    } else if (error.response?.status === 403) {
      console.error('🚨 403 Forbidden - CSRF/Authentication issue');
      console.error('🔍 Check CORS settings and authentication requirements');
    } else if (!error.response) {
      console.error('🌐 Network Error - Unable to reach backend server');
      console.error('🔍 Check if backend server is running and accessible');
    }
    
    // For 500 errors or network issues, return empty array instead of throwing
    if (error.response?.status >= 500 || !error.response) {
      console.log('📌 Returning empty feedback array due to server/network error');
      return [];
    }
    return [];
  }
};



export default api;